import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens, getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u3_x3_mix';

const TEXT_BLOCKS = [
  { key: 'block_titel', heading: 'MSR Funktionsbeschreibung: Integrierte Regelung – Licht & Schatten', body: '' },
  { key: 'block_einfuehrung', heading: 'Einführung', body: 'Integration von Beleuchtung und Beschattung maximiert visuellen & thermischen Komfort, reduziert Energie für Kunstlicht und Klimatisierung und erhöht Nutzerakzeptanz. Ziel: Gleichgewicht aus ausreichender Helligkeit, minimaler Blendung und reduzierter Wärmelast.' },
  { key: 'block_licht_funktionen', heading: 'Grundfunktionen Beleuchtung (Überblick)', body: 'Manuelles Schalten – Präsenzmelder – Konstantlichtregelung – Dimmfunktion – Szenensteuerung.' },
  { key: 'block_beschattung_funktionen', heading: 'Grundfunktionen Beschattung (Überblick)', body: 'Manuelles Auf/Ab – Zeitsteuerung – Sonnenautomatik – Lamellennachführung – Sicherheitsfunktionen (Wind/Regen/Frost).' },
  { key: 'block_zusammenspiel_intro', heading: 'Zusammenspiel – Mehrwert der Kopplung', body: 'Beschattung dient nicht nur als Sonnenschutz, sondern auch Lichtlenkung für tiefen Tageslichteintrag. Steuerung koordiniert Helligkeit, Blendung & Wärmeeintrag.' },
  { key: 'block_praesenz', heading: '1. Präsenzabhängige Gesamtsteuerung', body: 'Betritt Nutzer den Raum: Grundszene oder Konstantlichtregelung aktiv, Beschattung prüft Sonnenstatus. Verlassen: Licht aus/dimmen + Beschattung in energieoptimierte Position (Sommer geschlossen, Winter offen). Kurzzeit-Nachlauf konfigurierbar.' },
  { key: 'block_tageslicht_blendschutz', heading: '2. Optimierte Tageslichtnutzung mit Blendschutz', body: 'Hohe Einstrahlung → Beschattung fährt herunter, Lamellenwinkel lenkt diffuses Licht tief in den Raum. Konstantlichtregelung dimmt Kunstlicht proportional herunter → Minimierung Beleistungsenergie & Kühllast.' },
  { key: 'block_blendschutz_vorrang', heading: '3. Blendschutz hat Vorrang', body: 'Manuelles starkes Schließen (Präsentation) senkt Tageslicht → Konstantlichtregelung erhöht Kunstlicht zur Soll-Lux-Haltung. Manuelle Übersteuerung mit zeitlicher Begrenzung und Automatik-Rückfall.' },
  { key: 'block_komponenten', heading: 'Benötigte Komponenten', body: '• Präsenz-/Bewegungsmelder (ggf. integriert mit Helligkeit)\n• Innen-Helligkeitssensor für Konstantlicht\n• Außen-Wetterstation: Globalstrahlung, Wind, Regen, Temperatur optional GPS für Sonnenstand\n• Beleuchtungsaktoren (DALI/KNX)\n• Beschattungsaktoren / Jalousieaktoren\n• Logik-/Raumcontroller (Prioritäten, Verzögerungen, Szenen)\n• Bedienelemente (Taster, Touchpanel, Visualisierung)' },
  { key: 'block_energie', heading: 'Energieeffizienz durch Integration', body: '• Maximierung Tageslichtanteil\n• Reduktion Kühllast (vorverlagerter Sonnenschutz)\n• Nutzung solarer Gewinne im Winter\n• Präsenzabhängigkeit senkt Grundlast\n• Vermeidung simultaner Fehlbedienung (Licht an + Beschattung geschlossen bei ausreichendem Tageslicht).'},
  { key: 'block_parameter', heading: 'Wichtige Parameter & Konfiguration', body: '• Raum-Soll-Lux (Arbeitsbereich)\n• Präsenz-Nachlaufzeiten\n• Sonnen-Schwellwerte fassadenorientiert\n• Verzögerungen zur Entprellung (Wolkenzug)\n• Prioritäten (Sicherheit > Manuell > Automatik)\n• Szenendefinition (Arbeit, Präsentation, Reinigung)\n• Dauer manueller Übersteuerung (Timeout)'},
  { key: 'block_sicherheit', heading: 'Sicherheitsaspekte', body: '• Windvorrang: Sofortige Hochfahrt außenliegender Anlagen\n• Regensensor: Schutz textiler Behänge\n• Frostschutz: Blockiert kritische Fahrten\n• Hinderniserkennung optional\n• Elektrische Sicherheit / korrekte Absicherung.' },
  { key: 'block_zusammenfassung', heading: 'Zusammenfassung & Ausblick', body: 'Integrierte Licht- und Beschattungsregelung steigert Komfort, senkt Energie und optimiert Betriebsführung. Zukünftig: Selbstlernende Algorithmen & tiefere Kopplung mit Heizung/Kühlung/Lüftung für ganzheitliche Raumklimaregelung.' }
];

const IMAGE_BLOCKS = [{ key: 'img_0', src: '/assets/drawable/zzz_help.png', label: 'Integration Schema (Platzhalter)' }];

const M4_U3_X3_Mix: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = useCallback((key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) s = s.split('/').pop() || s;
    return `/assets/unit_custom/${s}`;
  }, [override]);

  const textBlocks = useMemo(() =>
    TEXT_BLOCKS.map(tb => ({ ...tb, body: override?.texts?.[tb.key] ?? tb.body })),
    [override]
  );

  const images = useMemo(() =>
    IMAGE_BLOCKS.map(img => ({ ...img, src: imgSrc(img.key, img.src) })),
    [imgSrc]
  );

  const fullText = useMemo(() => textBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [textBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Fehler beim Speichern: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (e: any) {
      console.error(e);
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : () => speak(), sx: { color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text } }, React.createElement(SpeakerIcon, { style: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '4.3.3 Licht & Beschattung – Integration', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: img.src, label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, textBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u3_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U3_X3_Mix;

